import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
  getCountryCallingCode,
} from "libphonenumber-js/min";
import type {CountryCode} from "libphonenumber-js/min";

export const DEFAULT_PHONE_COUNTRY: CountryCode = "NG";
export const DEFAULT_DIAL_CODE = "+234";

export interface ParsedPhone {
  /** Dial code with leading `+`, e.g. `"+1"` */
  numberCode: string;
  /** National digits only — no trunk prefix, no separators */
  phoneNumber: string;
  /** ISO country, populated when the library can disambiguate (e.g. +1 → "US" vs "CA") */
  country?: CountryCode;
}

function ensureLeadingPlus(value: string): string {
  return value.startsWith("+") ? value : `+${value}`;
}

/**
 * Split a stored phone (E.164 or loose digits) into dial code + national parts
 * for prefill. Disambiguates overlapping dial codes (+1 US vs CA, +44 UK vs Jersey)
 * via libphonenumber-js instead of a hand-rolled regex.
 */
export function parseE164(raw: string | null | undefined): ParsedPhone {
  if (!raw) {
    return {numberCode: DEFAULT_DIAL_CODE, phoneNumber: ""};
  }

  const parsed = parsePhoneNumberFromString(ensureLeadingPlus(raw));
  if (parsed && parsed.countryCallingCode) {
    return {
      numberCode: `+${parsed.countryCallingCode}`,
      phoneNumber: String(parsed.nationalNumber ?? ""),
      country: parsed.country,
    };
  }

  // Fallback for malformed saved values so the form still mounts.
  const match = raw.match(/^(\+?\d{1,4})(\d+)$/);
  if (match) {
    const code = match[1].startsWith("+") ? match[1] : `+${match[1]}`;
    const national = match[2].replace(/^0+/, "");
    return {numberCode: code, phoneNumber: national};
  }

  return {numberCode: DEFAULT_DIAL_CODE, phoneNumber: ""};
}

/**
 * Combine dial code + national number into E.164. Strips trunk prefixes
 * (e.g. UK leading `0`) when the library can infer the country from the
 * dial code; otherwise falls back to concatenation — matching the prior
 * behavior for valid inputs.
 */
export function toE164(numberCode: string, nationalNumber: string): string {
  const cleanedDial = numberCode.replace(/\D/g, "");
  const cleanedNational = nationalNumber.replace(/\D/g, "");

  if (!cleanedDial) return `+${cleanedNational}`;

  const parsed = parsePhoneNumberFromString(cleanedNational, {
    defaultCallingCode: cleanedDial,
  });
  if (parsed?.number) return parsed.number;

  return `+${cleanedDial}${cleanedNational}`;
}

/** Length-based soft check — suitable for on-change feedback in Phase 2. */
export function isPossible(numberCode: string, nationalNumber: string): boolean {
  const cleanedDial = numberCode.replace(/\D/g, "");
  const cleanedNational = nationalNumber.replace(/\D/g, "");
  if (!cleanedDial || !cleanedNational) return false;
  return isPossiblePhoneNumber(cleanedNational, {defaultCallingCode: cleanedDial});
}

/** Full validity — suitable for blur/submit gates in Phase 2. */
export function isValid(numberCode: string, nationalNumber: string): boolean {
  const cleanedDial = numberCode.replace(/\D/g, "");
  const cleanedNational = nationalNumber.replace(/\D/g, "");
  if (!cleanedDial || !cleanedNational) return false;
  return isValidPhoneNumber(cleanedNational, {defaultCallingCode: cleanedDial});
}

/** Dial code (with `+`) for a given ISO country. */
export function getDialCode(country: CountryCode): string {
  return `+${getCountryCallingCode(country)}`;
}
